<!--
  Created 11.06.2023
  Entity info component
-->
<template>
  <div
    class="basis-1/2 flex-col overflow-y-auto text-gray-800 @container">
    <!-- Entity top section, image and content version select dropdown -->
    <div
      class="relative aspect-[16/9] w-full"
      :class="{'group': !anyElementChanged}">
      <!-- Image -->
      <MediaFigure
        v-if="image"
        :image-alt="image.custom_properties?.alt_description"
        :fig-caption="image.name"
        class="max-h-[40vh] print:aspect-[3/1]"
        :image="image" />
      <!-- Image editing -->
      <div
        v-if="selectedElement && !anyElementChanged && canEdit"
        class="absolute bottom-0 right-0 w-full p-4 pb-24">
        <div class="mx-auto max-w-7xl">
          <media-select-single
            :selected="image"
            :settings="{
              entity: {
                name: entity.place?.name ?? entity.label ?? entity.name,
                get: entityType ? route('api.entity.media.index', {placeEntity: entity.id}) : route('api.destination.entity.media.index', {entity: entity.id}),
                upload: entityType ? route('api.entity.media.index', {placeEntity: entity.id}) : route('api.destination.entity.media.index', {entity: entity.id})
              }
            }"
            :default_search="(entity.place
              ? (entity.place?.name ?? entity.label ?? entity.name) + (destination ? (', ' + destination.name + ', ' + destination.parent?.name) : '')
              : (entity.label ?? entity.name) + (entity.parent?.name ? ', ' + entity.parent?.name : ''))"
            :cropable="typeof image !== 'string'"
            :coordinates="imageCrop ? imageCrop.coordinates : null"
            hide-media-update-buttons
            @update="updateMedia"
            @crop="cropped" />
        </div>
      </div>
      <!-- info and close button -->
      <span
        v-if="entityType"
        class="absolute left-5 top-2 text-sm text-white">{{ entityType }}</span>
      <XMarkIcon
        v-if="closable"
        class="absolute right-3 top-2 size-7 cursor-pointer p-0.5 text-gray-200 hover:text-white"
        @click="emit('close')" />
      <!-- Content changed warning -->
      <div
        v-if="anyElementChanged"
        class="absolute top-0 flex size-full flex-col items-center
                  justify-center bg-black bg-opacity-25 p-5
                  pb-[75px]">
        <span class="mb-4 block text-xl text-white">Content has changed! How to proceed?</span>
        <div class="w-full border border-dashed p-2 @md:flex @md:justify-between">
          <button
            class="justify-self-start rounded-md border border-gray-200
                      bg-gray-200 px-2 py-1 text-gray-500
                      hover:border-gray-300 hover:bg-gray-300 hover:text-gray-700
                      disabled:cursor-not-allowed disabled:border-gray-100 disabled:bg-gray-100 disabled:text-gray-300 disabled:opacity-75"
            :disabled="!canEdit"
            title="This content version belongs to your team or a team member, and you do not have rights to overwrite this version."
            @click="update">
            Overwrite version
          </button>
          <div class="mt-2 justify-self-end @md:mt-0">
            <button
              class="mr-2 rounded-md border border-gray-200
                        bg-gray-200 px-2 py-1 text-gray-500
                        hover:border-gray-300 hover:bg-gray-300 hover:text-gray-700"
              @click="cancelChanges=!cancelChanges; elementProposalProgramTextChanged=''; elementDescriptionChanged=''; elementNameChanged=''; elementHeadingChanged='';">
              Revert
            </button>
            <button
              class="rounded-md border border-purple-800
                        bg-purple-800 px-2 py-1 text-white
                        hover:border-purple-700 hover:bg-purple-700"
              @click="createAsNew">
              Save as new version
            </button>
          </div>
        </div>
      </div>
      <div class="absolute inset-x-0 bottom-0 flex flex-row items-center space-x-2 p-5">
        <!-- content version selection dropdown -->
        <SelectMenuCustomWithAvatar
          v-model="selectedElement"
          :elements="elements"
          :selected-element="selectedElement"
          title-alternative
          enable-create-options
          class="z-50 grow"
          @create-new-option="create"
          @selected-element-changed="cancelChanges=!cancelChanges; elementProposalProgramTextChanged=''; elementDescriptionChanged=''; elementNameChanged=''; elementHeadingChanged='';" />
        <!-- language switcher -->
        <div class="relative !-mr-1.5 !ml-1 w-9">
          <div
            class="flex h-9 w-full justify-center border border-transparent px-1 pb-1 pt-1.5"
            :class="{'rounded-md rounded-b-none border !border-gray-300 !border-b-gray-200 bg-teal-50 shadow-md':languageSelectorOpen}">
            <img
              v-if="languages"
              :src="languages[currentLanguage]?.flag"
              alt="Language flag."
              title="Click to get a list of available languages to switch to."
              class="h-4 w-6 shrink-0 cursor-pointer self-center rounded-sm object-cover drop-shadow-md"
              @click="languageSelectorOpen=!languageSelectorOpen">
          </div>
          <div
            v-if="languageSelectorOpen"
            class="absolute z-10 flex w-full flex-col space-y-1.5 rounded-md rounded-t-none border
              border-t-0 border-gray-300 bg-white px-1 pb-1.5 pt-2 shadow-md
              transition duration-150 ease-in-out">
            <img
              v-for="language in languagesFiltered"
              :src="language.flag"
              :alt="`${language.name} language flag.`"
              :title="`Click to change the language to ${language.name}.`"
              class="h-4 w-6 shrink-0 cursor-pointer self-center rounded-sm object-cover"
              @click="setCurrentLanguage(language.id); languageSelectorOpen=false;">
          </div>
        </div>
        <!-- content version settings -->
        <EntityVersionSettings
          v-model="selectedElement"
          :entity="entity"
          :entity-type="entityType"
          :entity-type-slug="entityTypeSlug"
          :can-delete="canDelete"
          :can-edit="canEdit"
          @update:title="(value) => updateEntityVersionTitle(value)"
          @update:default="(state) => updateEntityVersionDefault(state)"
          @delete:version="(value) => destroy(value)"
          @delete:entity="(value) => $emit('delete:entity', value)" />
      </div>
    </div>
    <!-- Entity texts -->
    <div
      v-if="selectedElement"
      class="relative border-t border-gray-100 px-3 py-4">
      <small
        v-if="destinations.length > 1"
        class="cursor-default px-2 text-gray-400">{{ entity.parent.name }}, {{ entity.parent.area }}</small>
      <TextEditableTextarea
        v-if="hasName.active"
        id="element-name"
        v-model="selectedElement.texts[currentLanguage].name"
        :placeholder="languageDefaultTexts[currentLanguage].name ?? 'Write your entity name here'"
        :show-buttons="false"
        :auto-save="false"
        :cancel-changes="cancelChanges"
        :saved-changes="savedChanges"
        :text-area-class="hasName.class"
        @edited="(content) => elementNameChanged = content" />
      <TextEditableTextarea
        v-if="hasHeading.active"
        id="element-heading"
        v-model="selectedElement.texts[currentLanguage].heading"
        :placeholder="languageDefaultTexts[currentLanguage].heading ?? 'Write your entity heading here'"
        :show-buttons="false"
        :auto-save="false"
        :cancel-changes="cancelChanges"
        :saved-changes="savedChanges"
        :text-area-class="hasHeading.class"
        @edited="(content) => elementHeadingChanged = content" />
      <TextEditableTextarea
        v-if="hasType.active"
        id="element-type"
        v-model="selectedElement.texts[currentLanguage].type"
        :auto-grow="false"
        :tab-rows="1"
        :placeholder="languageDefaultTexts[currentLanguage].type ?? 'Write your entity type here'"
        :show-buttons="false"
        :auto-save="false"
        :cancel-changes="cancelChanges"
        :saved-changes="savedChanges"
        :text-area-class="hasType.class"
        @edited="(content) => elementNameChanged = content" />
      <TextEditableTextarea
        v-if="hasDescription.active"
        id="element-description"
        v-model="selectedElement.texts[currentLanguage].description"
        :tab-rows="3"
        :placeholder="languageDefaultTexts[currentLanguage].description ?? 'Write your entity description here'"
        :show-buttons="false"
        :auto-save="false"
        :cancel-changes="cancelChanges"
        :saved-changes="savedChanges"
        :text-area-class="hasDescription.class"
        @edited="(content) => elementDescriptionChanged = content" />
      <hr
        v-if="hasProposalProgramText.active"
        class="my-4 border-gray-200 bg-gray-200">
      <TextEditableTextarea
        v-if="hasProposalProgramText.active"
        id="element-program-text"
        v-model="selectedElement.texts[currentLanguage].proposalProgramText"
        :tab-rows="2"
        :label="'Proposal program text'"
        :label-class="'ml-2'"
        :placeholder="languageDefaultTexts[currentLanguage].proposalProgramText ?? 'Write your entity proposal program text here'"
        :show-buttons="false"
        :auto-save="false"
        :cancel-changes="cancelChanges"
        :saved-changes="savedChanges"
        :text-area-class="hasProposalProgramText.class"
        @edited="(content) => elementProposalProgramTextChanged = content" />
    </div>
    <div
      v-else
      class="relative border-t border-gray-100 px-5 pb-6 pt-4">
      <p class="text-xl font-medium">
        No texts available. Please add one from the dropdown menu.
      </p>
    </div>
    <slot name="extra" />
  </div>
</template>

<script setup>
/* external imports */
import {
  computed, onMounted, ref, useAttrs, watch,
} from 'vue';
import {
  UserGroupIcon, UserIcon, XMarkIcon,
} from '@heroicons/vue/24/outline';
import { UserIcon as UserIconSolid } from '@heroicons/vue/24/solid';
import { notify } from 'notiwind';

/* internal imports */
import TextEditableTextarea from '@/Components/Forms/TextEditableTextarea.vue';
import SelectMenuCustomWithAvatar from '@/Components/Selectors/SelectMenuCustomWithAvatar.vue';
import MediaSelectSingle from '@/Components/Media/MediaPickerSingle.vue';
import BlurHash from '@/ComponentsDeprecated/BlurHash.vue';
import EntityVersionSettings from '@/Pages/Destination/Components/EntityVersionSettings.vue';
import {
  getCurrentLanguage, setCurrentLanguage,
} from '@/utils';
import MediaFigure from "@/Pages/Proposal/Components/MediaFigure.vue";

const {
  languages, user: currentUser, permissions,
} = useAttrs();

const props = defineProps({
  entity: {
    type: Object,
    required: true,
  },
  entityType: {
    type: String,
    required: false,
    default: '',
  },
  entityTypeSlug: {
    type: String,
    required: false,
    default: '',
  },
  destination: {
    type: Object,
    required: false,
    default: null,
  },
  destinations: {
    type: Array,
    required: false,
    default: () => [],
  },
  currentUser: {
    type: Object,
    required: true,
  },
  closable: {
    type: Boolean,
    required: false,
    default: false,
  },
  hasName: {
    type: Object,
    required: false,
    default: () => ({
      active: false,
      class: 'text-xl font-medium px-2 py-1 placeholder:italic',
    }),
  },
  hasHeading: {
    type: Object,
    required: false,
    default: () => ({
      active: false,
      class: 'text-xl font-medium px-2 py-1 placeholder:italic',
    }),
  },
  hasType: {
    type: Object,
    required: false,
    default: () => ({
      active: false,
      class: 'text-xs font-small px-2 py-1 placeholder:italic',
    }),
  },
  hasDescription: {
    type: Object,
    required: false,
    default: () => ({
      active: true,
      class: 'text-md font-normal px-2 py-1 placeholder:italic',
    }),
  },
  hasProposalProgramText: {
    type: Object,
    required: false,
    default: () => ({
      active: true,
      class: 'text-xs font-normal px-2 py-1 placeholder:italic',
    }),
  },
});

const emit = defineEmits(['delete:entity', 'update', 'createAsNew', 'create', 'close']);
const languageSelectorOpen = ref(false);

const entity = computed(() => props.entity);
const currentLanguage = computed(() => getCurrentLanguage());

const languagesFiltered = computed(() => Object.values(languages).filter((language) => language.id !== currentLanguage.value));

const languageDefaultTexts = {
  en: {
    name: 'Enter a name/title here',
    heading: 'Optional field for a heading',
    type: 'Enter a type here',
    description: 'Enter a description here',
    proposalProgramText: 'Enter a proposal program text here',
  },
  no: {
    name: 'Skriv inn et navn/tittel her',
    heading: 'Felt for en valgfri overskrift',
    type: 'Skriv inn en type her',
    description: 'Skriv inn en beskrivelse her.',
    proposalProgramText: 'Skriv inn en programtekst for tilbud her',
  },
  sv: {
    name: 'Skriv in ett namn/titel här',
    heading: 'Fält för en valfri rubrik',
    type: 'Skriv in en typ här',
    description: 'Skriv in en beskrivning här',
    proposalProgramText: 'Skriv in en programtext för erbjudanden här',
  },
  da: {
    name: 'Indtast et navn/titel her',
    heading: 'Felt til en valgfri overskrift',
    type: 'Indtast en type her',
    description: 'Indtast en beskrivelse her',
    proposalProgramText: 'Indtast en programtekst for tilbud her',
  },
};

/**
 *
 */
function currentDateLocalized() {
  const date = new Date();
  const dateOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  const timeOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };

  return `${date.toLocaleDateString(currentLanguage.value, dateOptions)} ${date.toLocaleTimeString(currentLanguage.value, timeOptions)}`;
}

/**
 * Content version elements handling
 */
const elements = computed(() => entity.value.content_versions.map((element) => {
  let icon;
  if (element.owner_type === 'App\\Models\\Team') {
    icon = UserGroupIcon;
  } else {
    icon = element.owner_id === props.currentUser.id ? UserIconSolid : UserIcon;
  }

  return {
    ...element,
    titleAlternative: `${element.owner.name} - ${element.title}`,
    icon,
  };
}));

const selectedElement = ref();
const elementNameChanged = ref('');
const elementHeadingChanged = ref('');
const elementProposalProgramTextChanged = ref('');
const elementDescriptionChanged = ref('');
const anyElementChanged = computed(() => elementNameChanged.value !== ''
  || elementHeadingChanged.value !== ''
  || elementProposalProgramTextChanged.value !== ''
  || elementDescriptionChanged.value !== '');
const cancelChanges = ref(false);
const savedChanges = ref(false);

/**
 * Content version media handling
 */
const image = ref(null);
const imageCrop = ref(null);

/**
 * Content version permissions handling
 */
const canEdit = computed(() => {
  if (!selectedElement.value) {
    return false;
  }
  if (selectedElement.value.owner_type === 'App\\Models\\Team' && (
    selectedElement.value.created_by === currentUser.id || permissions.find((permission) => (permission === 'remove members')))
  ) {
    return true;
  }

  return selectedElement.value.owner_type === 'App\\Models\\User' && selectedElement.value.owner_id === currentUser.id;
});

const canDelete = computed(() => {
  if (!selectedElement.value) {
    return false;
  }
  if (selectedElement.value.parent_type === 'App\\Models\\Place' && selectedElement.value.owner_type === 'App\\Models\\Team') {
    return false;
  }
  if (selectedElement.value.owner_type === 'App\\Models\\Team' && (
    selectedElement.value.created_by === currentUser.id || permissions.find((permission) => (permission === 'remove members')))
  ) {
    return true;
  }

  return selectedElement.value.owner_type === 'App\\Models\\User' && selectedElement.value.owner_id === currentUser.id;
});

/**
 *
 * @param media
 */
function updateMedia(media) {
  image.value = media;
  selectedElement.value.data.image = media;
  // reset any changes to text content if accidentally changed
  cancelChanges.value = !cancelChanges.value;
  elementProposalProgramTextChanged.value = '';
  elementDescriptionChanged.value = '';
  elementNameChanged.value = '';
  elementHeadingChanged.value = '';
  // save media to database
  axios.put(route('api.content-version.update', { contentVersion: selectedElement.value.id }), {
    ...selectedElement.value,
    data: {
      ...selectedElement.value.data,
      image: image.value,
    },
  }).then(() => {
    // notify user
    notify({
      type: 'success',
      title: 'Saved',
      text: `Image for version ${selectedElement.value.titleAlternative} saved`,
    }, 4000);
    // place image in selectedElement
    selectedElement.value = {
      ...selectedElement.value,
      data: {
        ...selectedElement.value.data,
        image: image.value,
      },
    };
    // updated elements array with new SelectedElement
    elements.value[elements.value.findIndex((element) => element.id === selectedElement.value.id)] = selectedElement.value;
    // reset changed states
    elementNameChanged.value = '';
    elementHeadingChanged.value = '';
    elementProposalProgramTextChanged.value = '';
    elementDescriptionChanged.value = '';
    // tell component that changes are saved
    savedChanges.value = true;
  }).catch((error) => {
    // notify user of error
    notify({
      type: 'danger',
      title: 'Something went wrong',
      text: `Could not save image for version ${selectedElement.value.titleAlternative}`,
    }, 4000);
    // log error
    console.error('save error: ', error);
  }).finally(() => {
    // reset saved changes state
    savedChanges.value = false;
  });
}

/**
 *
 * @param data
 */
function cropped(data) {
  imageCrop.value = data;
  selectedElement.value.data.media_crop = data;
  // reset any changes to text content if accidentally changed
  cancelChanges.value = !cancelChanges.value;
  elementProposalProgramTextChanged.value = '';
  elementDescriptionChanged.value = '';
  elementNameChanged.value = '';
  elementHeadingChanged.value = '';
  // save media to database
  axios.put(route('api.content-version.update', { contentVersion: selectedElement.value.id }), {
    ...selectedElement.value,
    data: {
      ...selectedElement.value.data,
      media_crop: imageCrop.value,
    },
  }).then(() => {
    // notify user
    notify({
      type: 'success',
      title: 'Saved',
      text: `Image crop for version ${selectedElement.value.titleAlternative} saved`,
    }, 4000);
    // updated elements array with new SelectedElement
    elements.value[elements.value.findIndex((element) => element.id === selectedElement.value.id)] = selectedElement.value;
    // reset changed states
    elementNameChanged.value = '';
    elementHeadingChanged.value = '';
    elementProposalProgramTextChanged.value = '';
    elementDescriptionChanged.value = '';
    // tell component that changes are saved
    savedChanges.value = true;
  }).catch((error) => {
    // notify user of error
    notify({
      type: 'danger',
      title: 'Something went wrong',
      text: `Could not save image crop for version ${selectedElement.value.titleAlternative}`,
    }, 4000);
    // log error
    console.error('save error: ', error);
  }).finally(() => {
    // reset saved changes state
    savedChanges.value = false;
  });
}

/**
 *
 * @param notifyText
 */
function update(notifyText = '') {
  axios.put(route('api.content-version.update', { contentVersion: selectedElement.value.id }), selectedElement.value).then(() => {
    // notify user
    notify({
      type: 'success',
      title: 'Saved',
      text: notifyText.length ? notifyText : `Content for version ${selectedElement.value.titleAlternative} saved`,
    }, 4000);
    // updated elements array with new SelectedElement
    elements.value[elements.value.findIndex((element) => element.id === selectedElement.value.id)] = selectedElement.value;
    // reset changed states
    elementNameChanged.value = '';
    elementHeadingChanged.value = '';
    elementProposalProgramTextChanged.value = '';
    elementDescriptionChanged.value = '';
    // tell component that changes are saved
    savedChanges.value = true;
  }).catch((error) => {
    // notify user of error
    notify({
      type: 'danger',
      title: 'Something went wrong',
      text: `Could not save content for version ${selectedElement.value.titleAlternative}`,
    }, 4000);
    // log error
    console.error('save error: ', error);
  }).finally(() => {
    // reset saved changes state
    savedChanges.value = false;
  });
}

/**
 *
 */
function createAsNew() {
  // reset original element
  const newElement = selectedElement.value;
  cancelChanges.value = !cancelChanges.value;
  elementProposalProgramTextChanged.value = '';
  elementDescriptionChanged.value = '';
  elementNameChanged.value = '';
  elementHeadingChanged.value = '';
  // create new element
  axios.post(route('api.content-version.store'), {
    parent_type: newElement.parent_type,
    parent_id: newElement.parent_id,
    owner_type: 'App\\Models\\User',
    owner_id: props.currentUser.id,
    standard: false,
    data: {
      ...newElement.data,
    },
    texts: {
      ...newElement.texts,
      [currentLanguage.value]: {
        ...newElement.texts[currentLanguage.value],
        name: elementNameChanged.value.length > 0 ? elementNameChanged.value : newElement.texts[currentLanguage.value].name,
        heading: elementHeadingChanged.value.length > 0 ? elementHeadingChanged.value : newElement.texts[currentLanguage.value].heading,
        description: elementDescriptionChanged.value.length > 0 ? elementDescriptionChanged.value : newElement.texts[currentLanguage.value].description,
        proposalProgramText: elementProposalProgramTextChanged.value.length > 0 ? elementProposalProgramTextChanged.value : newElement.texts[currentLanguage.value].proposalProgramText,
      },
    },
    title: `${currentDateLocalized()}`,
  }).then((response) => {
    // notify user
    notify({
      type: 'success',
      title: 'Created',
      text: `New version ${props.currentUser.name} - ${currentDateLocalized()} was created and saved!`,
    }, 4000);
    // updated elements array with new element
    elements.value.push({
      ...response.data, icon: UserIconSolid, title: `${currentDateLocalized()}`, titleAlternative: `${props.currentUser.name} - ${currentDateLocalized()}`,
    });
    // let parent know that new element was created
    emit('created', {
      ...response.data,
      owner: {
        id: props.currentUser.id,
        name: props.currentUser.name,
        email: props.currentUser.email,
      },
    });
    // reset changed states
    elementNameChanged.value = '';
    elementHeadingChanged.value = '';
    elementProposalProgramTextChanged.value = '';
    elementDescriptionChanged.value = '';
    // tell component that changes are saved
    savedChanges.value = true;
    // updated selected element
    selectedElement.value = elements.value[elements.value.length - 1];
  }).catch((error) => {
    // notify user of error
    notify({
      type: 'danger',
      title: 'Something went wrong',
      text: `Could not create new version ${props.currentUser.name} - ${currentDateLocalized()}`,
    }, 4000);
    // log error
    console.error('save error: ', error);
  }).finally(() => {
    // reset saved changes state
    savedChanges.value = false;
  });
}

/**
 *
 */
function create() {
  // reset original element
  cancelChanges.value = !cancelChanges.value;
  elementProposalProgramTextChanged.value = '';
  elementDescriptionChanged.value = '';
  elementNameChanged.value = '';
  elementHeadingChanged.value = '';
  axios.post(route('api.content-version.store'), {
    parent_type: (entity.value.place || entity.value.global) ? 'App\\Models\\PlaceEntity' : 'App\\Models\\Place',
    parent_id: entity.value.id,
    owner_type: 'App\\Models\\User',
    owner_id: props.currentUser.id,
    standard: elements.value.length <= 0,
    data: {},
    texts: {
      en: {
        heading: null,
        description: null,
        proposalProgramText: null,
      },
      no: {
        heading: null,
        description: null,
        proposalProgramText: null,
      },
      sv: {
        heading: null,
        description: null,
        proposalProgramText: null,
      },
      da: {
        heading: null,
        description: null,
        proposalProgramText: null,
      },
    },
    title: elements.value.length > 0 ? currentDateLocalized() : 'Default',
  }).then((response) => {
    // notify user
    notify({
      type: 'success',
      title: 'Created',
      text: `New option ${props.currentUser.name} - ${elements.value.length > 0 ? currentDateLocalized() : 'Default'} was created!`,
    }, 4000);
    // updated elements array with new element
    elements.value.push({
      ...response.data, icon: UserIconSolid, titleAlternative: `${props.currentUser.name} - ${elements.value.length > 0 ? currentDateLocalized() : 'Default'}`,
    });
    // let parent know that new element was created
    emit('created', {
      ...response.data,
      owner: {
        id: props.currentUser.id,
        name: props.currentUser.name,
        email: props.currentUser.email,
      },
    });
    // reset changed states
    elementNameChanged.value = '';
    elementHeadingChanged.value = '';
    elementProposalProgramTextChanged.value = '';
    elementDescriptionChanged.value = '';
    // tell component that changes are saved
    savedChanges.value = true;
    // updated selected element
    selectedElement.value = elements.value[elements.value.length - 1];
  }).catch((error) => {
    // notify user of error
    notify({
      type: 'danger',
      title: 'Something went wrong',
      text: `Could not create new option ${props.currentUser.name} - ${elements.value.length > 0 ? currentDateLocalized() : 'Default'} was created!`,
    }, 4000);
    // log error
    console.error('save error: ', error);
  }).finally(() => {
    // reset saved changes state
    savedChanges.value = false;
  });
}

/**
 *
 * @param id
 */
function destroy(id) {
  axios.delete(route('api.content-version.destroy', { contentVersion: id })).then(() => {
    // notify user
    notify({
      type: 'success',
      title: 'Deleted',
      text: `Content version ${selectedElement.value.titleAlternative} deleted`,
    }, 4000);
    // remove the element from elements array
    const objIndex = elements.value.findIndex((element) => element.id === id);
    if (objIndex > -1) {
      elements.value.splice(objIndex, 1);
    }
    // set selected element to first element in array
    // eslint-disable-next-line prefer-destructuring
    selectedElement.value = elements.value[0];
    // reset changed states
    elementNameChanged.value = '';
    elementHeadingChanged.value = '';
    elementProposalProgramTextChanged.value = '';
    elementDescriptionChanged.value = '';
    // tell component that changes are saved
    savedChanges.value = true;
  }).catch((error) => {
    // notify user of error
    notify({
      type: 'danger',
      title: 'Something went wrong',
      text: `Could not delete content version ${selectedElement.value.titleAlternative}`,
    }, 4000);
    // log error
    console.error('save error: ', error);
  }).finally(() => {
    // reset saved changes state
    savedChanges.value = false;
  });
}

/**
 *
 * @param newTitle
 */
function updateEntityVersionTitle(newTitle) {
  selectedElement.value.title = newTitle;
  selectedElement.value.titleAlternative = `${(selectedElement.value.owner?.name ?? currentUser.name)} - ${newTitle}`;
  update();
}

/**
 *
 * @param state
 */
function updateEntityVersionDefault(state = true) {
  selectedElement.value.standard = state !== false;
  update(`Content version ${(state ? 'set' : 'remove')} as default`);
}

onMounted(() => {
  // get the default/standard text from database, if available
  const dbSelectedVersion = (elements.value.length > 0
    ? (elements.value.find((version) => (version.owner_type === 'App\\Models\\User' && version.owner_id === currentUser.id && version.standard === true))
        ?? elements.value.find((version) => (version.owner_type === 'App\\Models\\Team' && version.standard === true))
        ?? elements.value.find((version) => version.standard === true))
      ?? elements.value[0] : null
  );
  // set currently selected element to default/standard text
  if (dbSelectedVersion) {
    selectedElement.value = elements.value[elements.value.findIndex((e) => e.id === dbSelectedVersion.id)];
  } else if (elements.value.length > 0) {
    // eslint-disable-next-line prefer-destructuring
    selectedElement.value = elements.value[0];
  }
  // check if selectedElement.value.texts has all languages present in texts, and if not add them
  if (!selectedElement.value.texts[currentLanguage]) {
    Object.keys(languages).forEach((language) => {
      if (!selectedElement.value.texts[language]) {
        selectedElement.value.texts[language] = {
          name: '',
          heading: '',
          description: '',
          proposalProgramText: '',
        };
      }
    });
  }
  // if no name is set, set it to the entity name
  if (selectedElement.value && !selectedElement?.value?.texts[currentLanguage.value]?.name) {
    selectedElement.value.texts[currentLanguage.value].name = (entity.value.place?.name ?? entity.value.name) ?? '';
  }
});

watch(() => selectedElement.value, () => {
  image.value = {
    selectedElementTitle: selectedElement.value.texts[currentLanguage.value].name,
    ...selectedElement.value.data.image,
  };
  imageCrop.value = selectedElement.value?.data.media_crop ?? null;
});

</script>
